import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Play, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import ConciergeBand from "../components/ConciergeBand";

export default function Destinations() {
  const [articles, setArticles] = useState([]);
  const [region, setRegion] = useState("All");
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/articles").then(r => setArticles(r.data));
    api.get("/downloads").then(r => setCounts(r.data)).catch(() => {});
  }, []);

  const track = (slug) => {
    api.post(`/articles/${slug}/download`).then(r => setCounts(c => ({ ...c, [slug]: r.data.count }))).catch(() => {});
  };

  const regions = useMemo(() => {
    const present = new Set(articles.map(a => a.region));
    const preferred = ["Global", "Europe", "Middle East", "Asia", "Other"];
    const ordered = preferred.filter(r => present.has(r));
    const extras = [...present].filter(r => !preferred.includes(r));
    return ["All", ...ordered, ...extras];
  }, [articles]);
  const filtered = region === "All" ? articles : articles.filter(a => a.region === region);

  return (
    <main data-testid="destinations-page">
      <Seo title="Destinations" path="/destinations" description="Browse every published travel article from The Where To Go — Porto, Seville, Dubai, Abu Dhabi, Saudi Arabia, China, London at Christmas and more."/>
      <section className="max-w-8xl mx-auto px-6 md:px-12 pt-6 md:pt-10 pb-8 border-b border-line">
        <div className="overline text-olive mb-5">The archive</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">Destinations</h1>
        <p className="mt-6 max-w-xl text-muted text-lg leading-relaxed">Articles sorted by date (most recent first) and places. Articles are linked to downloadable PDFs and curated videos to give you an oversight of each destination and highlight where to go and what to see.</p>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="region-filters">
          {regions.map(r => (
            <button key={r} onClick={() => setRegion(r)} className={`px-4 py-2 text-[0.72rem] uppercase tracking-[0.18em] font-semibold border ${region === r ? "bg-ink text-bg border-ink" : "border-line hover:border-ink"}`} data-testid={`region-filter-${r}`}>{r}</button>
          ))}
        </div>
      </section>

      <section className="max-w-8xl mx-auto px-6 md:px-12 py-10 md:py-12 grid md:grid-cols-2 gap-12">
        {filtered.map(a => (
          <div key={a.slug} className="group" data-testid={`destination-card-${a.slug}`}>
            <Link to={`/articles/${a.slug}`} className="block">
              <div className="aspect-[5/4] overflow-hidden">
                <img src={a.card_cover || a.cover} alt={a.title} className="w-full h-full object-cover img-hover"/>
              </div>
              <div className="mt-5 flex items-center justify-between text-sm">
                <div className="overline text-olive">{a.destination} · {a.region}</div>
                <div className="text-muted">{a.published}</div>
              </div>
              <div className="font-serif text-3xl md:text-4xl mt-2 leading-tight group-hover:text-olive transition-colors">{a.title}</div>
              <div className="text-muted mt-3 leading-relaxed">{a.subtitle}</div>
            </Link>
            <div className="mt-5 flex items-center gap-4">
              {a.pdf_url && (
                <Link to={`/articles/${a.slug}`} className="text-[0.78rem] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5 hover:text-olive" data-testid={`destination-read-${a.slug}`}>
                  <BookOpen size={14} strokeWidth={1.8}/> Read
                </Link>
              )}
              {a.video_url && (
                <a href={a.video_url} target="_blank" rel="noreferrer" className="text-[0.78rem] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5 text-muted hover:text-clay" data-testid={`destination-video-${a.slug}`}>
                  <Play size={14} strokeWidth={1.8}/> Watch
                </a>
              )}
              {a.pdf_url && (
                <a href={a.pdf_url} target="_blank" rel="noreferrer" download onClick={() => track(a.slug)} className="text-[0.78rem] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5 text-muted hover:text-olive" data-testid={`destination-pdf-${a.slug}`}>
                  <Download size={14} strokeWidth={1.8}/> Download
                </a>
              )}
              {/* Public download count hidden — see /admin/downloads dashboard */}
            </div>
          </div>
        ))}
      </section>

      <ConciergeBand/>
    </main>
  );
}
